import React, { useRef, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { AnimatedCanvas } from '../atoms/AnimatedCanvas'
import PingPongGameController from '../molecules/PingPongGameController'
import { AuthError } from '../../auth/authTools'

interface AnimatedBackgroundProps {
  children?: React.ReactNode
  error?: AuthError | null
  className?: string
}

export const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  children,
  error,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [canvasState, setCanvasState] = useState<{
    canvas: HTMLCanvasElement | null
    ctx: CanvasRenderingContext2D | null
  }>({
    canvas: null,
    ctx: null,
  })

  const location = useLocation()
  
  // Extract error from navigation state if available
  const navigationError = location.state?.error as AuthError | undefined
  const displayError = error || navigationError

  const handleCanvasReady = useCallback((canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
    console.log('Canvas ready:', canvas.width, 'x', canvas.height)
    setCanvasState({ canvas, ctx })
  }, [])

  return (
    <div 
      className={`fixed inset-0 w-screen h-screen flex items-center justify-center bg-gray-900 ${className}`}
      data-component="animated-background"
      data-positioning="fixed-fullscreen"
    >
      {/* Game Container - Centered with border and margin */}
      <div 
        className="relative w-full h-full max-w-[calc(100vw-160px)] max-h-[calc(100vh-160px)] m-20 sm:m-16 md:m-20"
        data-component="game-container"
        data-sizing="responsive-with-margins"
      >
        {/* Game Border */}
        <div 
          className="relative w-full h-full border-2 border-white/30 rounded-xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 shadow-2xl"
          data-component="game-border"
          data-styling="bordered-container"
        >
          {/* Canvas positioned to fill the bordered container */}
          <div 
            className="absolute inset-0"
            data-component="canvas-wrapper"
            data-positioning="absolute-fill"
          >
            <AnimatedCanvas 
              ref={canvasRef}
              className="w-full h-full"
              onCanvasReady={handleCanvasReady}
              data-component="animated-canvas"
              data-sizing="fill-parent"
            />
          </div>
          
          {/* Game Controller */}
          {canvasState.canvas && canvasState.ctx && (
            <PingPongGameController
              canvas={canvasState.canvas}
              ctx={canvasState.ctx}
              error={displayError}
            />
          )}
        </div>
      </div>
      
      {/* Login Form Overlay - Absolutely positioned in center */}
      <div className="absolute inset-0 z-20 flex flex-col justify-center items-center p-8">
        <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
          {children}
        </div>
      </div>
    </div>
  )
} 